import { Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Link, MaterialPalette, TextField, Typography } from '@nutrien/cxp-components';
import { Auth } from 'aws-amplify';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { sentryCaptureException } from '../../utilities';
import { SignUpSteps } from './SignUpFlow';

interface Props {
  goToStep: (step: SignUpSteps) => void;
  userName: string;
  email: string;
}

const useStyles = makeStyles({
  root: {
    background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
    border: 0,
    borderRadius: 3,
    boxShadow: '0 3px 5px 2px rgba(255, 105, 135, .3)',
    color: MaterialPalette.common.white,
    height: 48,
    padding: '0 30px',
  },
  box: {
    height: '100vh',
    display: 'flex',
    minHeight: '800px',
    alignItems: 'center',
    zIndex: 2,
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    padding: '0px 20px',
  },
  linkContainer: {
    marginTop: '20px',
  },
  title: {
    textAlign: 'center',
    width: '100%',
  },
  subtitle: {
    textAlign: 'center',
    margin: '50px 0px 0px 0px',
    padding: '20px 0px 20px 0px',
    width: '100%',
  },
  button: {
    width: '190px',
    margin: '0 auto',
    marginTop: '50px',
    justifyContent: 'flex-start',
  },
  linkMargin: {
    marginTop: '20px',
  },
  emailLink: {
    textDecoration: 'none',
  },
  space: {
    marginTop: '10px',
  },
  space_btn: {
    marginTop: '36px',
    '& .diffUserLink': {
      '&:disabled': {
        backgroundColor: 'rgba(255, 255, 255, 0.48)',
      },
    },
  },
  space_resend: {
    marginTop: '30px',
    textAlign: 'center',
    '& #resendCodeLink': {
      textDecoration: 'none',
      marginLeft: 4,
    },
    fontWeight: 700,
  },
  confirmAccountButtonGrid: {
    width: '100%',
  },
  confirmLabel: {
    color: MaterialPalette.common.black,
  },
});

const VerifyCode = ({ userName, email }: Props) => {
  const classes = useStyles();
  const [code, setCode] = useState<string>('');
  const history = useHistory();
  const [sendingRequest, setSendingRequest] = useState(false);
  const [codeError, setCodeError] = useState<string>('');

  const handleChange = (event: React.FocusEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setCode(value);
  };

  const handleSubmit = async () => {
    setSendingRequest(true);
    try {
      await Auth.confirmSignUp(userName, code as string);
      history.push('/login');
    } catch (err) {
      setSendingRequest(false);
      const error = err as { code: string; message: string; name: string };
      if ('code' in error) {
        switch (error.code) {
          case 'InvalidPasswordException':
            setCodeError('Password not long enough');
            return 'Password not long enough';
          case 'CodeMismatchException':
            setCodeError('Invalid verification code provided, please try again.');
            return 'Invalid verification code provided, please try again.';
          case 'LimitExceededException':
            setCodeError('Attempt limit exceeded, please try after after 15 minute');
            return 'Attempt limit exceeded, please try after after 15 minute';
        }
      }
      setSendingRequest(false);
    }
  };

  const handleResend = async () => {
    try {
      await Auth.resendSignUp(userName);
    } catch (error) {
      console.error('Error in Resending Code ', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: VerifyCode.tsx ~ line 125 ~ handleResend ~');
    }
  };

  const emailSlice = () => {
    let i = 0;
    do {
      i++;
    } while (email[i] != '@');
    return email[i + 1];
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleSubmit();
    }
  };

  const getConfirmationView = () => {
    return (
      <Grid container spacing={0} className={classes.linkContainer} justifyContent="center">
        <Typography fontWeight={700} variant="body1" className={classes.subtitle}>
          {`We have send you a code by email to ${email[0]}***@${emailSlice()}***.com.
                    Enter it below to confirm your account. 
              `}
        </Typography>

        <Grid item xs={12} className={classes.space}>
          <TextField
            noMinHeight
            size="small"
            autoFocus
            onChange={handleChange}
            onKeyPress={handleKeyPress}
            label="Verification Code"
            placeholder="Enter Code"
            error={codeError !== ''}
            errorText={codeError}
            data-testid="verificationCode"
          />
        </Grid>

        <div className={classes.confirmAccountButtonGrid}>
          <Grid className={classes.space_btn}>
            <Button
              variant="contained"
              color="primary"
              disabled={!code}
              onClick={handleSubmit}
              className="diffUserLink"
              isLoading={sendingRequest}
              noMinHeight
              data-testid="confirmButton"
            >
              <Typography fontWeight={700} className={classes.confirmLabel}>
                Confirm Account
              </Typography>
            </Button>
          </Grid>
          <Grid className={classes.space_resend}>
            Didn't receive a code?
            <Link
              to=""
              onClick={handleResend}
              color="primary"
              id="resendCodeLink"
              data-testid="resendlink"
            >
              Resend Code
            </Link>
          </Grid>
        </div>
      </Grid>
    );
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        {getConfirmationView()}
      </Grid>
    </Grid>
  );
};

export default VerifyCode;
